<template>
	<div>
		<div v-if="!paymentAccepted">
			<p class="h1 mt-4">Merci !</p>
			<p class="h2 mb-5">
				Vos informations ont bien été enregistrées.
				<br />
				Si vous souhaitez poursuivre, merci de procéder au paiement.
			</p>

			<form @submit.stop.prevent="payWithCard()" id="stripe-form">
				<div id="card-element" />

				<button id="stripe-btn" :disabled="loading">
					<div class="spinner hidden" id="spinner" v-if="loading" />

					<span id="button-text" v-if="!loading">Payer</span>
				</button>

				<b-form-invalid-feedback :state="!cardError.state">
					{{ cardError.msg }}
				</b-form-invalid-feedback>

				<b-alert :show="queryError" variant="danger" dismissible>
					Nous sommes désolés mais nous ne sommes pas en mesure de communiquer avec le système de paiement pour le moment.
					Veuillez réessayer ultérieurement.
				</b-alert>
			</form>

			<div class="d-block d-xl-none mb-5">
				<CreationSummary
				:company="services.creation.company"
				:particulars="services.creation.particulars"
				:packs="services.creation.packs"
				:options="services.creation.options"
				:price="services.creation.price"
				/>
			</div>
		</div>

		<div v-if="paymentAccepted">
			<p class="h1 mt-4">Paiement validé !</p>
			<p class="h2 mb-4">
				Votre commande a été effectuée avec succès !
			</p>
			<p>
				Vous allez recevoir un email de confirmation contenant votre nouveau contrat sous forme de PDF.
				<br />
				Merci et à bientôt !
			</p>
		</div>
	</div>
</template>

<script>
	import { mapState } from 'vuex';
	import axios from 'axios';

	import CreationSummary from './CreationSummary.vue';

	export default {
		components: {
			CreationSummary
		},
		data() {
			return {
				card: null,
				clientSecret: null,
				queryError: false,
				loading: false,
				cardError: {
					state: false,
					msg: null
				},
				paymentAccepted: false
			}
		},
		methods: {
			tabTitle(index) {
				if (index === 5) {
					return "+5";
				} else {
					index ++;
					const title = index.toString();
					return title;
				}
			},
			priceWithTaxes () {
				if (this.services.creation.price.withDomiciliation) {
					return Math.round(this.services.creation.price.withOptions + this.services.domiciliation.price.initial * (1 + 20 / 100));
				} else {
					return Math.round(this.services.creation.price.withOptions * (1 + 20 / 100));
				}
			},
			priceWithDomiciliationTaxesNWarrantyDeposit () {
				return this.priceWithTaxes() + this.services.domiciliation.price.warrantyDeposit;
			},
			priceForStripe() {
				if (this.services.creation.price.withDomiciliation) {
					return this.priceWithDomiciliationTaxesNWarrantyDeposit()  * 100;
				} else {
					return this.priceWithTaxes()  * 100;
				}
			},
			activePack(packs) {
				let activePack = null

				packs.forEach((pack) => {
					if (pack.active === true)  {
						activePack = pack;
					}
				});

				return activePack;
			},
			activeOptions(options) {
				let activeOptions = [];

				options.forEach((option) => {
					if (option.active === true)  {
						activeOptions.push(option);
					}
				});

				return activeOptions;
			},
			translateCurrency(currency) {
				if (currency === "€") {
					return 'eur';
				}
			},
			payWithCard() {
				this.loading = true;

				this.stripe.js
				.confirmCardPayment(this.clientSecret, {
					payment_method: {
						card: this.card
					}
				})
				.then((result) => {
					if (result.error) {
						this.showError(result.error.message);
					} else {
						this.orderComplete();
					}
				});
			},
			orderComplete() {
				this.paymentAccepted = true;

				if (this.services.creation.company.headOffice.location === 'Domiciliation via Assistel') {
					this.$store.commit('updateDomiciliationAddress');
				}

				let requestTarget = this.apiDomain + '/send-email';

				axios.post(requestTarget, {
					type        : 'creationOrder',
					mailService : this.mailService,
					mailOptions : {
						from    : this.mailService.auth.user,
						to      : this.services.creation.company.representative.mailAddress,
						cc      : this.mailService.auth.user,
						subject : 'Commande effectuée !',
						html    : `
							<p style="font-family: Arial; font-size: 14px;">
								Votre commande a bien été effectuée, merci ! 😀
								<br />
								Vous trouverez vos statuts au format PDF en tant que pièce jointe à ce mail.
								<br />
								Nous vous contacterons très prochainement pour la suite de votre dossier.
								<br />
								Excellente journée.
								<br /> <br />
								Bienvenue chez Assistel !
							</p>
						`
					},
					pdfInfos : this.services.creation
				});
			},
			showError(errorMsgText) {
				this.loading = false;

				Object.assign(this.cardError, {
					state: true,
					msg: errorMsgText
				});

				setTimeout(() => {
					Object.assign(this.cardError, {
						state: false,
						msg: ""
					});
				}, 4000);
			},
		},
		computed: {
			...mapState(['services', 'apiDomain', 'stripe', 'mailService'])
		},
		created() {
			let requestTarget = this.apiDomain + '/create-payment-intent';

			axios.post(requestTarget, {
				sk : this.stripe.sk,
				amount: this.priceForStripe(),
				currency: this.translateCurrency(this.services.currency),
				description: "Création d'entreprise"
			})
			.then((response) => {
				const elements = this.stripe.js.elements();

				const style = {
					base: {
						color: "#32325d",
						fontFamily: 'Lato, Arial, sans-serif',
						fontSmoothing: "antialiased",
						fontSize: "16px",
						"::placeholder": {
								color: "#32325d"
						}
					},
					invalid: {
						fontFamily: 'Lato, Arial, sans-serif',
						color: "#dc3545",
						iconColor: "#dc3545"
					}
				};

				const card = elements.create("card", { style: style });
				card.mount("#card-element");

				this.card = card;
				this.clientSecret = response.data.clientSecret;
			})
			.catch(() => {
				this.queryError = true;
			});
		}
	}
</script>

<style lang="scss">
	#stripe-form
	{
		width: 30vw;
		min-width: 500px;
		box-shadow: 0px 0px 0px 0.5px rgba(50, 50, 93, 0.1),
		0px 2px 5px 0px rgba(50, 50, 93, 0.1), 0px 1px 1.5px 0px rgba(0, 0, 0, 0.07);
		border-radius: 7px;
		padding: 40px;
		margin: auto;

		&input
		{
			border-radius: 6px;
			margin-bottom: 6px;
			padding: 12px;
			border: 1px solid rgba(50, 50, 93, 0.1);
			height: 44px;
			font-size: 16px;
			width: 100%;
			background: white;
		}
	}

	#card-element
	{
		border-radius: 4px 4px 0 0 ;
		padding: 12px;
		border: 1px solid rgba(50, 50, 93, 0.1);
		height: 44px;
		width: 100%;
		background: white;
	}

	#payment-request-button
	{
		margin-bottom: 32px;
	}

	/* Buttons and links */
	#stripe-btn
	{
		background: #ad233f;
		color: #ffffff;
		border-radius: 0 0 4px 4px;
		border: 0;
		padding: 12px 16px;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		display: block;
		transition: all 0.2s ease;
		box-shadow: 0px 4px 5.5px 0px rgba(0, 0, 0, 0.07);
		width: 100%;

		&:hover
		{
			background: #8d1d33;
		}

		&:disabled
		{
			opacity: 0.5;
			cursor: default;
		}
	}

	/* spinner/processing state, errors */
	.spinner,
	.spinner:before,
	.spinner:after
	{
		border-radius: 50%;
	}

	.spinner
	{
		color: #ffffff;
		font-size: 22px;
		text-indent: -99999px;
		margin: 0px auto;
		position: relative;
		width: 20px;
		height: 20px;
		box-shadow: inset 0 0 0 2px;
		-webkit-transform: translateZ(0);
		-ms-transform: translateZ(0);
		transform: translateZ(0);
	}

	.spinner:before,
	.spinner:after
	{
		position: absolute;
		content: "";
	}

	.spinner:before
	{
		width: 10.4px;
		height: 20.4px;
		background: #ad233f;
		border-radius: 20.4px 0 0 20.4px;
		top: -0.2px;
		left: -0.2px;
		-webkit-transform-origin: 10.4px 10.2px;
		transform-origin: 10.4px 10.2px;
		-webkit-animation: loading 2s infinite ease 1.5s;
		animation: loading 2s infinite ease 1.5s;
	}

	.spinner:after
	{
		width: 10.4px;
		height: 10.2px;
		background: #ad233f;
		border-radius: 0 10.2px 10.2px 0;
		top: -0.1px;
		left: 10.2px;
		-webkit-transform-origin: 0px 10.2px;
		transform-origin: 0px 10.2px;
		-webkit-animation: loading 2s infinite ease;
		animation: loading 2s infinite ease;
	}

	@-webkit-keyframes loading
	{
		0% {
			-webkit-transform: rotate(0deg);
			transform: rotate(0deg);
		}
		100% {
			-webkit-transform: rotate(360deg);
			transform: rotate(360deg);
		}
	}
	@keyframes loading
	{
		0% {
			-webkit-transform: rotate(0deg);
			transform: rotate(0deg);
		}
		100% {
			-webkit-transform: rotate(360deg);
			transform: rotate(360deg);
		}
	}

	@media only screen and (max-width: 600px)
	{
		#stripe-form {
			width: 80vw;
		}
	}
</style>
